import envConfig from '@nile/home/src/Config/env';
import {
  ChooseAttachments,
  BackgroundIcon,
  DisabledTag,
  toast,
  getDownloadURL,
  ALL_MODULES,
  getTranslatedText
} from '@nile/components';
import { Modal, TextContainer } from '@superops/ui-design-system';
import React, { useCallback, useState, useEffect } from 'react';
import styled from 'styled-components';
import { fileSizes } from '@nile/banyan/src/Constants/admin';
import { SVGIcon } from '@superops/super-icons';

const getImageDimentions = (file) => {
  const reader = new FileReader();
  return new Promise((resolve, reject) => {
    let width;
    let height;

    const onError = () => {
      reject({ height, width });
    };

    reader.onload = (file) => {
      const image = new Image();
      image.src = reader.result;
      // get image dimensions
      image.onload = function() {
        height = this.height;
        width = this.width;
        resolve({ height, width });
      };

      image.onerror = onError;
      image.onabort = onError;
    };
    reader.onerror = onError;
    reader.onabort = onError;
    // reads the file
    reader.readAsDataURL(file);
  });
};

function Attachments(props) {
  const [active, setActive] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState(null);
  const { modalActivator, client, intl, height, width } = props;
  const [loading, setLoading] = useState(props.loading);
  useEffect(() => {
    setLoading(props.loading);
  }, [props.loading]);
  const handleChange = useCallback(() => setActive(!active), [active]);

  const uploadDocuments = useCallback(() => {
    props.handleUpload(uploadedFiles, handleChange);
  });

  const handleCancel = useCallback(() => {
    setUploadedFiles(null);
    handleChange();
  });

  const updateFiles = (updatedFiles) => {
    setUploadedFiles(updatedFiles);
  };

  const checkFileSize = (file) => {
    return file?.size < fileSizes.fileUploadLimit;
  };

  const customValidator = async (files) => {
    let validatedFiles = [...files];

    const filterPromises = files.map((file) =>
      file.type.startsWith('image/') ? getImageDimentions(file) : true
    );
    const resolvedFilterPromises = await Promise.all(filterPromises);

    validatedFiles = files.filter((file, idx) => {
      if (!checkFileSize(file))
        toast(
          'warning',
          getTranslatedText(intl, 'admin.upload.size.max.error', {
            size: `${fileSizes.fileUploadLimit / 1000000}MB`
          })
        );

      if (file.type.startsWith('image/')) {
        const {
          height: imageHeight,
          width: imageWidth
        } = resolvedFilterPromises[idx];
        const isDimensionValid = !(imageHeight > height || imageWidth > width);
        if (!isDimensionValid)
          toast(
            'warning',
            getTranslatedText(intl, 'admin.upload.dimensions.max.error', {
              width,
              height
            })
          );
        return isDimensionValid && checkFileSize(file);
      }
      return checkFileSize(file);
    });

    return validatedFiles;
  };

  return (
    <div className="full-width flex actr jctr">
      {modalActivator ? (
        <div onClick={handleChange} id={props.id}>
          {modalActivator}
        </div>
      ) : client.logoFileName ? (
        <div
          onClick={handleChange}
          id={props.id}
          className="flex cursor-pointer position-relative min-width-40 min-height-40 visibilityContainer"
        >
          <Logo className="logo" client={client} url={client.downloadURL} />
          <div
            vertical
            className="overlay fx center jctr visibilityActions opacity8 bg-black text-white position-absolute top-0 fhp fw tcenter"
          >
            <SVGIcon name="CloudUpload" color="#ffffff" />
          </div>
        </div>
      ) : (
        <div
          onClick={handleChange}
          id={props.id}
          className="flex cursor-pointer position-relative min-width-40 min-height-40 visibilityContainer"
        >
          <div className="fx center" style={{ opacity: 0.4 }}>
            <SVGIcon name="ClientLogo" size="xl" />
          </div>
          <div className="overlay fx center jctr visibilityActions opacity8 bg-black text-white position-absolute top-0 fhp fw tcenter">
            <SVGIcon name="CloudUpload" color="#ffffff" />
          </div>
        </div>
      )}
      <Modal
        // activator={activator}//check why this is not rendering
        open={active}
        onClose={handleChange}
        title={props.title || 'Upload Logo'}
        primaryAction={{
          content: 'Upload',
          loading: loading,
          onAction: uploadDocuments
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: handleCancel
          }
        ]}
      >
        <Modal.Section>
          <TextContainer>
            <p className="mb10">
              {props.description || 'Select logo for the client'}
            </p>
          </TextContainer>
          <ChooseAttachments
            validTypes="image/jpeg, image/png"
            type="image"
            updateFiles={updateFiles}
            customValidator={customValidator}
          />
        </Modal.Section>
      </Modal>
    </div>
  );
}

function Logo(props) {
  const { client } = props;
  const { logoFileName, accountId, downloadURL } = client;
  const module = ALL_MODULES.clientAvatar;
  const details = {
    fileName: logoFileName,
    clientId: accountId
  };
  const fallbackUrl = getDownloadURL({ module, details });
  const downloadUrl = downloadURL || fallbackUrl;
  return (
    <div className="flex center jcenter full-width">
      <img
        className="max-height-40 mw-100"
        src={downloadUrl}
        url={downloadUrl}
        alt="loading"
      />
    </div>
  );
}

export default Attachments;
